import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Card,
  Col,
  Row,
  Button,
  Form,
  Modal,
  Alert,
  InputGroup,
  Spinner,
  Dropdown,
  Pagination,
} from "react-bootstrap";
import {
  collection,
  addDoc,
  getDocs,
  doc,
  getDoc,
  deleteDoc,
} from "firebase/firestore";
import { db, auth } from "../firebaseConfig";
import { onAuthStateChanged } from "firebase/auth";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import { FaTrash, FaSort, FaSortUp, FaSortDown } from "react-icons/fa";

export default function PayrollManagement() {
  const [startDate, setStartDate] = useState(new Date());
  const [payrolls, setPayrolls] = useState([]);
  const [filteredPayrolls, setFilteredPayrolls] = useState([]);
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [filterUser, setFilterUser] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPayroll, setNewPayroll] = useState({
    biweeklyHours: "",
    biweeklySalary: "",
  });
  const [currentUserRole, setCurrentUserRole] = useState("");
  const [rolePermissions, setRolePermissions] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [operationLoading, setOperationLoading] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [payrollToDelete, setPayrollToDelete] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: "date", direction: "desc" });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  const dropdownRef = useRef(null);

  useEffect(() => {
    const fetchUserRole = async () => {
      onAuthStateChanged(auth, async (user) => {
        if (user) {
          const userDocRef = doc(db, "Users", user.uid);
          const userDoc = await getDoc(userDocRef);

          if (userDoc.exists()) {
            const userRole = userDoc.data().role || "";
            setCurrentUserRole(userRole);

            if (userRole === "admin") {
              setRolePermissions({ canManagePayrolls: true });
            } else {
              const roleDocRef = doc(db, "Roles", userRole);
              const roleDoc = await getDoc(roleDocRef);

              if (roleDoc.exists()) {
                setRolePermissions(roleDoc.data().permissions);
              } else {
                setRolePermissions(null);
              }
            }
          }
        }
      });
    };

    fetchUserRole();
  }, []);

  useEffect(() => {
    if (rolePermissions && !rolePermissions.canManagePayrolls && currentUserRole !== "admin") {
      setAccessDenied(true);
    }
  }, [rolePermissions]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const payrollQuery = await getDocs(collection(db, "payrolls"));
        const payrollData = payrollQuery.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setPayrolls(payrollData);

        const usersQuery = await getDocs(collection(db, "Users"));
        const usersData = usersQuery.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setUsers(usersData);
        setFilteredUsers(usersData);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    filterPayrolls();
  }, [payrolls, startDate, filterUser, sortConfig, currentPage]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownVisible(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const filterPayrolls = () => {
    const selectedMonth = startDate.getMonth();
    const selectedYear = startDate.getFullYear();

    let filtered = payrolls.filter((payroll) => {
      const payrollDate = new Date(payroll.date);
      return (
        payrollDate.getMonth() === selectedMonth &&
        payrollDate.getFullYear() === selectedYear
      );
    });

    if (filterUser) {
      filtered = filtered.filter((payroll) => payroll.username === filterUser);
    }

    // Sorting
    if (sortConfig !== null) {
      filtered.sort((a, b) => {
        let aKey = a[sortConfig.key];
        let bKey = b[sortConfig.key];

        if (sortConfig.key === "date") {
          aKey = new Date(aKey);
          bKey = new Date(bKey);
        } else if (typeof aKey === "string") {
          aKey = aKey.toLowerCase();
          bKey = bKey.toLowerCase();
        }

        if (aKey < bKey) {
          return sortConfig.direction === "asc" ? -1 : 1;
        }
        if (aKey > bKey) {
          return sortConfig.direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    }

    setFilteredPayrolls(filtered);
    setCurrentPage(1);
  };

  const handleDateChange = (e) => {
    setStartDate(new Date(e.target.value));
  };

  const handleSearchUsers = (searchText) => {
    setDropdownVisible(true);
    if (!searchText) {
      setFilteredUsers(users);
      setFilterUser("");
    } else {
      const filtered = users.filter((user) =>
        user.fullName.toLowerCase().includes(searchText.toLowerCase())
      );
      setFilteredUsers(filtered);
    }
  };

  const handleUserSelect = (userId) => {
    const selected = users.find((user) => user.id === userId);
    setSelectedUser(selected);
    setFilterUser(selected.fullName);
    setDropdownVisible(false);
  };

  const handleAddPayroll = async () => {
    if (
      !newPayroll.username ||
      !newPayroll.email ||
      !newPayroll.date ||
      !newPayroll.biweeklyHours ||
      !newPayroll.biweeklySalary
    ) {
      alert("Please fill in all fields before adding the payroll.");
      return;
    }

    try {
      setOperationLoading(true);
      const createdAt = new Date().toISOString();
      const payrollWithMetadata = {
        ...newPayroll,
        CreatedAt: createdAt,
        CreatedBy: currentUserRole,
      };

      await addDoc(collection(db, "payrolls"), payrollWithMetadata);

      setNewPayroll({
        biweeklyHours: "",
        biweeklySalary: "",
      });
      setShowModal(false);
      refreshPayrolls();
    } catch (error) {
      console.error("Error adding payroll:", error);
      alert("Failed to add payroll. Please try again.");
    } finally {
      setOperationLoading(false);
    }
  };

  const refreshPayrolls = async () => {
    setLoading(true);
    try {
      const payrollQuery = await getDocs(collection(db, "payrolls"));
      const payrollData = payrollQuery.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setPayrolls(payrollData);
    } catch (error) {
      console.error("Error refreshing payrolls:", error);
    } finally {
      setLoading(false);
    }
  };

  const requestSort = (key) => {
    let direction = "asc";
    if (
      sortConfig &&
      sortConfig.key === key &&
      sortConfig.direction === "asc"
    ) {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (!sortConfig || sortConfig.key !== key) {
      return <FaSort />;
    }
    if (sortConfig.direction === "asc") {
      return <FaSortUp />;
    }
    return <FaSortDown />;
  };

  // Pagination
  const paginatedPayrolls = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredPayrolls.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredPayrolls, currentPage]);

  const totalPages = Math.ceil(filteredPayrolls.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const openDeleteModal = (payroll) => {
    setPayrollToDelete(payroll);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!payrollToDelete) return;
    try {
      setOperationLoading(true);
      await deleteDoc(doc(db, "payrolls", payrollToDelete.id));
      setShowDeleteModal(false);
      setPayrollToDelete(null);
      refreshPayrolls();
    } catch (error) {
      console.error("Error deleting payroll:", error);
      alert("Failed to delete payroll. Please try again.");
    } finally {
      setOperationLoading(false);
    }
  };

  if (accessDenied) {
    return (
      <>
        <Header />
        <div className="main main-app p-3 p-lg-4">
          <h1 className="text-center text-danger">403 Forbidden</h1>
          <p className="text-center">
            You do not have permission to access this page.
          </p>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="main main-app p-3 p-lg-4 p-xxl-5">
        <h1 className="text-center mb-4 text-primary fw-bold">Payroll Management</h1>
        <Alert variant="info" className="text-center shadow-sm rounded-3 p-3 fs-6">
          Select a month and filter by username to view payrolls or add a new
          one.
        </Alert>

        <Card className="mb-4 shadow-lg rounded-4 p-4 border-0 bg-light">
          <Row className="align-items-center g-3">
            <Col md={3} sm={6}>
              <Form.Group>
                <Form.Label className="fw-semibold fs-6 text-primary">Filter by Month</Form.Label>
                <Form.Control
                  type="month"
                  value={`${startDate.getFullYear()}-${String(
                    startDate.getMonth() + 1
                  ).padStart(2, "0")}`}
                  onChange={handleDateChange}
                  className="shadow-sm rounded-3 border-primary"
                  style={{ transition: "border-color 0.3s ease" }}
                />
              </Form.Group>
            </Col>
            <Col md={5} sm={6} ref={dropdownRef} className="position-relative">
              <Form.Group>
                <Form.Label className="fw-semibold fs-6 text-primary">Search Username</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Type to search"
                  onChange={(e) => handleSearchUsers(e.target.value)}
                  onFocus={() => setDropdownVisible(true)}
                  className="shadow-sm rounded-3 border-primary"
                  value={filterUser}
                  style={{ transition: "border-color 0.3s ease" }}
                />
                {dropdownVisible && (
                  <div className="dropdown-menu w-100 show rounded-3 shadow-lg border-0">
                    {filteredUsers.length > 0 ? (
                      filteredUsers.map((user) => (
                        <div
                          key={user.id}
                          className="dropdown-item py-2"
                          onClick={() => handleUserSelect(user.id)}
                          style={{ cursor: "pointer", fontWeight: "500" }}
                        >
                          {user.fullName}
                        </div>
                      ))
                    ) : (
                      <div className="dropdown-item py-2">No results found</div>
                    )}
                  </div>
                )}
              </Form.Group>
            </Col>
            <Col md={4} sm={12} className="d-flex align-items-start pt-4 pt-md-0">
              <Button
                variant="success"
                className="shadow-lg rounded-3 fs-6 fw-semibold"
                onClick={() => setShowModal(true)}
                style={{ transition: "background-color 0.3s ease", height: "60px", width: "200px", border: "2px solid #28a745" }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = "#28a745cc"}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = ""}
              >
                Add New Payroll
              </Button>
            </Col>
          </Row>
        </Card>

        {loading ? (
          <div className="text-center my-5">
            <Spinner animation="border" variant="primary" />
            <p>Loading payrolls...</p>
          </div>
        ) : filteredPayrolls.length > 0 ? (
          <>
            <Row xs={1} md={2} lg={3} className="g-4">
              {paginatedPayrolls.map((payroll) => (
                <Col key={payroll.id}>
                  <Card className="shadow-lg rounded-4 h-100 position-relative border-0" style={{background: "linear-gradient(135deg, #f6d365 0%, #fda085 100%)"}}>
                    <Card.Body>
                      <Card.Title className="d-flex justify-content-between align-items-center text-white fw-bold">
                        <span>{payroll.username}</span>
                        <Button
                          variant="link"
                          className="text-white p-0"
                          onClick={() => openDeleteModal(payroll)}
                          aria-label={`Delete payroll for ${payroll.username}`}
                          style={{filter: "drop-shadow(0 0 2px rgba(0,0,0,0.5))"}}
                        >
                          <FaTrash size={20} />
                        </Button>
                      </Card.Title>
                      <Card.Text className="text-white fw-semibold fs-6">
                        <strong>Email:</strong> {payroll.email}
                      </Card.Text>
                      <Card.Text className="text-white fw-semibold fs-6">
                        <strong>Biweekly Hours:</strong> {payroll.biweeklyHours}
                      </Card.Text>
                      <Card.Text className="text-white fw-semibold fs-6">
                        <strong>Biweekly Salary:</strong> ${payroll.biweeklySalary}
                      </Card.Text>
                      <Card.Text className="text-white fw-semibold fs-6">
                        <strong>Date:</strong> {payroll.date}
                      </Card.Text>
                      <Card.Text className="text-white fw-semibold fs-6">
                        <strong>Created By:</strong> {payroll.CreatedBy}
                      </Card.Text>
                      <Card.Text>
                        <small className="text-white fw-light fs-7">
                          Created At: {new Date(payroll.CreatedAt).toLocaleString()}
                        </small>
                      </Card.Text>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>

            {totalPages > 1 && (
              <Pagination className="justify-content-center mt-4">
                {[...Array(totalPages)].map((_, idx) => (
                  <Pagination.Item
                    key={idx + 1}
                    active={idx + 1 === currentPage}
                    onClick={() => handlePageChange(idx + 1)}
                  >
                    {idx + 1}
                  </Pagination.Item>
                ))}
              </Pagination>
            )}
          </>
        ) : (
          <p className="text-center my-5">No payrolls found for the selected filters.</p>
        )}

        <Modal show={showModal} onHide={() => setShowModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Add Payroll</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Username</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter username"
                  value={newPayroll.username || ""}
                  onChange={(e) =>
                    setNewPayroll({ ...newPayroll, username: e.target.value })
                  }
                  className="shadow-sm"
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  placeholder="Enter email"
                  value={newPayroll.email || ""}
                  onChange={(e) =>
                    setNewPayroll({ ...newPayroll, email: e.target.value })
                  }
                  className="shadow-sm"
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Payment Date</Form.Label>
                <Form.Control
                  type="date"
                  value={newPayroll.date || ""}
                  onChange={(e) =>
                    setNewPayroll({ ...newPayroll, date: e.target.value })
                  }
                  className="shadow-sm"
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Biweekly Hours</Form.Label>
                <Form.Control
                  type="number"
                  placeholder="Enter hours"
                  value={newPayroll.biweeklyHours || ""}
                  onChange={(e) =>
                    setNewPayroll({
                      ...newPayroll,
                      biweeklyHours: e.target.value,
                    })
                  }
                  className="shadow-sm"
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Biweekly Salary</Form.Label>
                <Form.Control
                  type="number"
                  placeholder="Enter biweekly salary"
                  value={newPayroll.biweeklySalary || ""}
                  onChange={(e) =>
                    setNewPayroll({
                      ...newPayroll,
                      biweeklySalary: e.target.value,
                    })
                  }
                  className="shadow-sm"
                />
              </Form.Group>
              {operationLoading && (
                <div className="text-center">
                  <Spinner animation="border" variant="primary" />
                  <p>Processing...</p>
                </div>
              )}
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleAddPayroll}
              disabled={operationLoading}
            >
              Add Payroll
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal
          show={showDeleteModal}
          onHide={() => setShowDeleteModal(false)}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>Confirm Delete</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            Are you sure you want to delete payroll for{" "}
            <strong>{payrollToDelete?.username}</strong>?
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowDeleteModal(false)}
              disabled={operationLoading}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={confirmDelete}
              disabled={operationLoading}
            >
              Delete
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
      <Footer />
    </>
  );
}
